<script setup lang="ts">
import { computed } from 'vue';

import { getColorByStaffId } from '@/lib/utils';
import type { Member } from '@/types';

const props = defineProps<{
  avatarList: Member[];
  maxVisible?: number;
}>();

const MAX_VISIBLE_AVATARS = computed(() => props.maxVisible ?? 3);

const visibleAvatars = computed<Member[]>(() => {
  return props.avatarList.slice(0, MAX_VISIBLE_AVATARS.value);
});

const overflowCount = computed<number>(() => {
  return props.avatarList.length - MAX_VISIBLE_AVATARS.value;
});
</script>

<template>
  <div class="avatar-stack position-relative d-flex">
    <div
      v-for="(avatar, index) in visibleAvatars"
      :key="avatar.id"
      :style="{ zIndex: index, transform: `translateX(${index * -0.5}rem)` }"
    >
      <img
        v-if="avatar?.avatarUrl"
        :src="avatar?.avatarUrl"
        :alt="`${avatar.firstname}-${avatar.lastname}-avatar`"
        class="avatar"
        :style="{ backgroundColor: getColorByStaffId(avatar.id) }"
      />
      <div
        v-else
          class="avatar flex-center"
          :style="{ background: getColorByStaffId(avatar?.id) }"
        >
        <span style="font-size: var(--font-xxs); font-weight: 500; letter-spacing: -1px;">
          {{ avatar?.firstName ? avatar.firstName.charAt(0).toUpperCase() : avatar?.name?.trim().split(/\s+/)[0]?.charAt(0).toUpperCase() ?? '' }}
          {{ avatar?.lastName ? avatar.lastName.charAt(0).toUpperCase() : avatar?.name?.trim().split(/\s+/)[1]?.charAt(0).toUpperCase() ?? '' }}
        </span>
      </div>
    </div>

    <div
      v-if="overflowCount > 0"
      class="overflow-badge flex-center"
      :style="{ 
        transform: `translateX(${visibleAvatars.length * -0.5}rem)`, 
        zIndex: visibleAvatars.length, 
      }"
    >
      <span style="font-size: var(--font-xxs); font-weight: 500;">+{{ overflowCount }}</span>
    </div>
  </div>
</template>

<style scoped>
.avatar-stack {
  transition: transform 0.2s ease;
}

.avatar {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 100%;
  object-fit: cover;
  border: 1px solid var(--ch-white);
  color: var(--ch-white);
}

.overflow-badge {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 100%;
  border: 1px solid var(--ch-white);
  background-color: var(--ch-avatar-bg);
  color: #2b2b2b;
  flex-shrink: 0;
}
</style>
